/* ==========================================================================
   RIVIERE theme layer — interactions (vanilla JS, no dependencies)
   ========================================================================== */
(function () {
  'use strict';

  const RIVIERE = (window.RIVIERE = window.RIVIERE || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesign = () => !!(window.Shopify && window.Shopify.designMode);
  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.prototype.slice.call(r.querySelectorAll(s));
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const raf = (fn) => {
    let t = null;
    return function () {
      if (t) return;
      t = requestAnimationFrame(() => {
        t = null;
        fn();
      });
    };
  };
  const ARROW_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  const ARROW_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';
  RIVIERE.icons = { left: ARROW_L, right: ARROW_R };

  /* ---------- Money formatting (market-aware) ---------- */
  RIVIERE.formatMoney = function (cents) {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const locale = document.documentElement.lang || undefined;
    try {
      const fraction = cents % 100 === 0 ? 0 : 2;
      return new Intl.NumberFormat(locale, { style: 'currency', currency: cur, minimumFractionDigits: fraction, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + cur;
    }
  };

  /* ---------- Header: height variable + transparent → solid ---------- */
  function initHeader() {
    const section = qs('.section-header');
    const wrap = qs('.riviere-header');
    if (!section || !wrap) return;
    const setH = () => document.documentElement.style.setProperty('--riviere-header-h', section.offsetHeight + 'px');
    setH();
    if (window.ResizeObserver) new ResizeObserver(setH).observe(section);
    window.addEventListener('resize', raf(setH));

    if (!wrap.hasAttribute('data-riviere-transparent')) return;
    let hover = false;
    const update = () => {
      const open =
        !!qs('.riviere-header details[open]') ||
        document.body.classList.contains('overflow-hidden-mobile') ||
        document.body.classList.contains('overflow-hidden-tablet') ||
        document.body.classList.contains('overflow-hidden-desktop');
      const solid = window.scrollY > 8 || hover || open;
      wrap.classList.toggle('riviere-header--solid', solid);
    };
    update();
    window.addEventListener('scroll', raf(update), { passive: true });
    wrap.addEventListener('mouseenter', () => { hover = true; update(); });
    wrap.addEventListener('mouseleave', () => { hover = false; setTimeout(update, 50); });
    wrap.addEventListener('toggle', update, true);
    new MutationObserver(update).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(update).observe(wrap, { subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  /* ---------- Reveal on scroll ---------- */
  let revealIO = null;
  RIVIERE.reveal = function (root = document) {
    const els = qsa('.riviere-reveal:not(.is-in)', root);
    if (!document.body.classList.contains('riviere-reveal-on') || reduceMotion.matches || !('IntersectionObserver' in window) || isDesign()) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); } }),
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    els.forEach((el) => revealIO.observe(el));
  };

  /* ---------- Parallax (light) ---------- */
  function initParallax() {
    const items = () => qsa('[data-riviere-parallax]');
    if (reduceMotion.matches) return;
    const run = () => {
      const vh = window.innerHeight;
      items().forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const speed = parseFloat(el.dataset.riviereParallax) || 0.08;
        const p = (r.top + r.height / 2 - vh / 2) / vh;
        const target = el.firstElementChild || el;
        target.style.transform = 'translate3d(0,' + (p * -100 * speed).toFixed(2) + '%,0)';
      });
    };
    window.addEventListener('scroll', raf(run), { passive: true });
    window.addEventListener('resize', raf(run));
    run();
    RIVIERE.parallax = run;
  }

  /* ---------- <riviere-rail> drag / arrows / progress ---------- */
  class RiviereRail extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.viewport = qs('.riviere-rail__viewport', this);
      if (!this.viewport) return;
      this.prev = qs('[data-rail-prev]', this);
      this.next = qs('[data-rail-next]', this);
      this.bar = qs('.riviere-rail__progress', this);
      this.update = raf(this.update.bind(this));
      this.viewport.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));
      this.drag();
      this.update();
      if (this.dataset.autoplay && !reduceMotion.matches) this.auto(parseInt(this.dataset.autoplay, 10) * 1000);
    }
    step(dir) {
      const item = qs('.riviere-rail__item', this);
      const gap = parseFloat(getComputedStyle(qs('.riviere-rail__track', this)).columnGap || 0) || 0;
      const w = item ? item.getBoundingClientRect().width + gap : this.viewport.clientWidth * 0.8;
      const per = Math.max(1, Math.floor((this.viewport.clientWidth + gap) / w));
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      let left = this.viewport.scrollLeft + dir * w * per;
      if (this.dataset.loop && dir > 0 && this.viewport.scrollLeft >= max - 4) left = 0;
      if (this.dataset.loop && dir < 0 && this.viewport.scrollLeft <= 4) left = max;
      this.viewport.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    auto(ms) {
      let timer = setInterval(() => this.step(1), ms);
      const stop = () => clearInterval(timer);
      const start = () => { stop(); timer = setInterval(() => this.step(1), ms); };
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('touchstart', stop, { passive: true });
    }
    update() {
      const v = this.viewport;
      const max = v.scrollWidth - v.clientWidth;
      const isStatic = max <= 2;
      this.classList.toggle('is-static', isStatic);
      if (this.prev) this.prev.disabled = !this.dataset.loop && v.scrollLeft <= 2;
      if (this.next) this.next.disabled = !this.dataset.loop && v.scrollLeft >= max - 2;
      if (this.bar) {
        const thumb = clamp((v.clientWidth / v.scrollWidth) * 100, 8, 100);
        const p = max > 0 ? v.scrollLeft / max : 0;
        this.bar.style.setProperty('--riviere-rail-thumb', thumb + '%');
        this.bar.style.setProperty('--riviere-rail-x', ((p * (100 - thumb)) / thumb) * 100 + '%');
      }
    }
    drag() {
      const v = this.viewport;
      let down = false, startX = 0, startLeft = 0, moved = false;
      v.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startLeft = v.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) { moved = true; v.classList.add('is-dragging'); }
        if (moved) v.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          setTimeout(() => v.classList.remove('is-dragging'), 30);
          const item = qs('.riviere-rail__item', this);
          if (item) {
            const w = item.getBoundingClientRect().width + 24;
            v.scrollTo({ left: Math.round(v.scrollLeft / w) * w, behavior: 'smooth' });
          }
        }
      });
      v.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
    }
  }
  if (!customElements.get('riviere-rail')) customElements.define('riviere-rail', RiviereRail);

  /* ---------- <riviere-hscroll> pinned horizontal scroll story ---------- */
  class RiviereHScroll extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.pin = qs('.riviere-hs__pin', this);
      this.viewport = qs('.riviere-hs__viewport', this);
      this.track = qs('.riviere-hs__track', this);
      this.big = qs('.riviere-hs__bigword', this);
      this.bar = qs('.riviere-hs__progress', this);
      this.count = qs('[data-hs-current]', this);
      if (this.dataset.direction === 'right' && this.track) {
        Array.from(this.track.children).reverse().forEach((c) => this.track.appendChild(c));
      }
      this.panels = qsa('.riviere-hs__panel', this);
      const bp = parseInt(this.dataset.breakpoint || '750', 10);
      this.mqMobile = window.matchMedia('(max-width: ' + (bp - 1) + 'px)');
      this.mqMobile.addEventListener && this.mqMobile.addEventListener('change', () => this.layout());
      this.onScroll = raf(this.render.bind(this));
      this.onResize = raf(this.layout.bind(this));
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      if (this.viewport) this.viewport.addEventListener('scroll', raf(this.renderSwipe.bind(this)), { passive: true });
      if (this.viewport) ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((ev) => this.viewport.addEventListener(ev, () => { this._moved = true; }, { passive: true }));
      if (window.ResizeObserver) new ResizeObserver(this.onResize).observe(this.track);
      qsa('img', this).forEach((img) => img.complete || img.addEventListener('load', this.onResize, { once: true }));
      this.layout();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }
    get reverse() {
      return this.dataset.direction === 'right';
    }
    get pinned() {
      const mode = this.mqMobile.matches ? this.dataset.mobile : this.dataset.desktop;
      return mode === 'pinned' && !reduceMotion.matches;
    }
    layout() {
      const pinned = this.pinned;
      this.classList.toggle('riviere-hs--pinned', pinned);
      this.classList.toggle('riviere-hs--swipe', !pinned);
      if (pinned) {
        this.track.style.transform = '';
        const distance = Math.max(0, this.track.scrollWidth - window.innerWidth);
        this.distance = distance;
        this.style.height = (window.innerHeight + distance) + 'px';
      } else {
        this.style.height = '';
        this.track.style.transform = '';
        if (this.big) this.big.style.transform = 'translateY(-50%)';
        if (this.reverse && this.viewport && !this._moved) this.viewport.scrollLeft = this.viewport.scrollWidth;
      }
      this.render();
      this.renderSwipe();
    }
    progress(p) {
      /* p = how far the visitor has travelled (0 at the start, 1 at the end), whichever direction the panels move */
      if (this.bar) this.bar.style.setProperty('--riviere-p', p.toFixed(4));
      if (this.count && this.panels.length) {
        const idx = clamp(Math.round(p * (this.panels.length - 1)) + 1, 1, this.panels.length);
        this.count.textContent = String(idx).padStart(2, '0');
      }
    }
    render() {
      if (!this.pinned) return;
      const r = this.getBoundingClientRect();
      const total = this.offsetHeight - window.innerHeight;
      const p = total > 0 ? clamp(-r.top / total, 0, 1) : 0;
      const shift = this.reverse ? (1 - p) : p;
      this.track.style.transform = 'translate3d(' + (-shift * this.distance).toFixed(1) + 'px,0,0)';
      if (this.big) this.big.style.transform = 'translate3d(' + (-shift * this.distance * 0.35).toFixed(1) + 'px,-50%,0)';
      this.progress(p);
    }
    renderSwipe() {
      if (this.pinned || !this.viewport) return;
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      const f = max > 0 ? this.viewport.scrollLeft / max : 0;
      this.progress(this.reverse ? 1 - f : f);
    }
  }
  if (!customElements.get('riviere-hscroll')) customElements.define('riviere-hscroll', RiviereHScroll);

  /* ---------- <riviere-hero> slideshow ---------- */
  class RiviereHero extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.slides = qsa('.riviere-hero__slide', this);
      this.dots = qsa('.riviere-hero__dot', this);
      this.index = 0;
      this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
      if (this.slides.length < 2) return;
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));
      if (this.dataset.autoplay === 'true' && !reduceMotion.matches) this.play();
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.dataset.autoplay === 'true' && this.play());
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.dataset.blockId === e.detail.blockId);
        if (i > -1) { this.pause(); this.go(i); }
      });
    }
    go(i, user) {
      const n = this.slides.length;
      i = (i + n) % n;
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      if (this.dots[this.index]) { this.dots[this.index].classList.remove('is-active'); this.dots[this.index].setAttribute('aria-current', 'false'); }
      this.index = i;
      this.slides[i].classList.add('is-active');
      this.slides[i].removeAttribute('aria-hidden');
      if (this.dots[i]) {
        const d = this.dots[i];
        d.classList.remove('is-active'); void d.offsetWidth; d.classList.add('is-active');
        d.setAttribute('aria-current', 'true');
      }
      qsa('video', this.slides[i]).forEach((v) => v.play && v.play().catch(() => {}));
      if (user && this.timer) this.play();
    }
    play() { this.pause(); this.timer = setInterval(() => this.go(this.index + 1), this.speed); }
    pause() { clearInterval(this.timer); this.timer = null; }
  }
  if (!customElements.get('riviere-hero')) customElements.define('riviere-hero', RiviereHero);

  /* ---------- <riviere-marquee> fill to width ---------- */
  class RiviereMarquee extends HTMLElement {
    connectedCallback() {
      const track = qs('.riviere-mq__track', this);
      const group = qs('.riviere-mq__group', this);
      if (!track || !group || this._init) return;
      this._init = true;
      let guard = 0;
      while (group.scrollWidth < window.innerWidth * 1.2 && guard < 10 && group.children.length) {
        qsa(':scope > *', group).slice(0, group.dataset.count || 99).forEach((c) => group.appendChild(c.cloneNode(true)));
        guard++;
      }
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }
  }
  if (!customElements.get('riviere-marquee')) customElements.define('riviere-marquee', RiviereMarquee);

  /* ---------- <riviere-hotspots> lookbook ---------- */
  class RiviereHotspots extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.spots = qsa('.riviere-spot', this);
      this.links = qsa('[data-spot-link]', this);
      this.spots.forEach((s) => {
        const btn = qs('.riviere-spot__btn', s);
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(s.dataset.spot); });
        s.addEventListener('mouseenter', () => window.matchMedia('(hover: hover)').matches && this.open(s.dataset.spot));
      });
      this.links.forEach((l) => l.addEventListener('mouseenter', () => this.open(l.dataset.spotLink)));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.open(null); });
      document.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.open(null); });
    }
    toggle(id) { const s = this.spots.find((x) => x.dataset.spot === id); this.open(s && s.classList.contains('is-open') ? null : id); }
    open(id) {
      this.spots.forEach((s) => { const on = s.dataset.spot === id; s.classList.toggle('is-open', on); qs('.riviere-spot__btn', s).setAttribute('aria-expanded', on); });
      this.links.forEach((l) => l.classList.toggle('is-active', l.dataset.spotLink === id));
    }
  }
  if (!customElements.get('riviere-hotspots')) customElements.define('riviere-hotspots', RiviereHotspots);

  /* ---------- <riviere-statement> words light up on scroll ---------- */
  class RiviereStatement extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const el = qs('.riviere-statement__text', this);
      if (!el || this.dataset.effect !== 'reveal' || reduceMotion.matches) return;
      const walk = (node) => {
        Array.from(node.childNodes).forEach((n) => {
          if (n.nodeType === 3) {
            const frag = document.createDocumentFragment();
            n.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
              else { const s = document.createElement('span'); s.className = 'w'; s.textContent = part; frag.appendChild(s); }
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1) walk(n);
        });
      };
      walk(el);
      this.words = qsa('.w', el);
      const run = raf(() => {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const p = clamp((vh * 0.85 - r.top) / (r.height + vh * 0.35), 0, 1);
        const lit = Math.round(p * this.words.length);
        this.words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
      });
      window.addEventListener('scroll', run, { passive: true });
      run();
    }
  }
  if (!customElements.get('riviere-statement')) customElements.define('riviere-statement', RiviereStatement);

  /* ---------- <riviere-video> click to play ---------- */
  class RiviereVideo extends HTMLElement {
    connectedCallback() {
      const btn = qs('.riviere-video__play', this);
      if (!btn || this._init) return;
      this._init = true;
      btn.addEventListener('click', () => {
        const tpl = qs('template', this);
        if (tpl) {
          const holder = qs('.riviere-video__holder', this) || this;
          holder.appendChild(tpl.content.cloneNode(true));
        }
        const v = qs('video', this);
        if (v) { v.muted = false; v.controls = true; v.loop = false; v.currentTime = 0; v.play().catch(() => {}); }
        this.classList.add('is-playing');
      });
    }
  }
  if (!customElements.get('riviere-video')) customElements.define('riviere-video', RiviereVideo);

  /* ---------- <riviere-tabs> ---------- */
  class RiviereTabs extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const tabs = qsa('[role="tab"]', this);
      tabs.forEach((t) => t.addEventListener('click', () => {
        tabs.forEach((x) => {
          const on = x === t;
          x.setAttribute('aria-selected', on);
          const p = document.getElementById(x.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
          if (on && p) qsa('riviere-rail', p).forEach((r) => r.update && r.update());
        });
      }));
    }
  }
  if (!customElements.get('riviere-tabs')) customElements.define('riviere-tabs', RiviereTabs);

  /* ---------- <riviere-countdown> ---------- */
  class RiviereCountdown extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const end = Date.parse(this.dataset.end || '');
      if (isNaN(end)) { this.hidden = !isDesign(); return; }
      const parts = { d: qs('[data-d]', this), h: qs('[data-h]', this), m: qs('[data-m]', this), s: qs('[data-s]', this) };
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        let t = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (t <= 0) {
          clearInterval(this.timer);
          if (this.dataset.hideExpired === 'true' && !isDesign()) { const host = this.closest('[data-riviere-countdown-host]') || this; host.hidden = true; }
        }
        const d = Math.floor(t / 86400); t %= 86400;
        const h = Math.floor(t / 3600); t %= 3600;
        const m = Math.floor(t / 60); const s = t % 60;
        if (parts.d) parts.d.textContent = pad(d);
        if (parts.h) parts.h.textContent = pad(h);
        if (parts.m) parts.m.textContent = pad(m);
        if (parts.s) parts.s.textContent = pad(s);
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
  }
  if (!customElements.get('riviere-countdown')) customElements.define('riviere-countdown', RiviereCountdown);

  /* ---------- Cart helpers (add, refresh drawer / page) ---------- */
  RIVIERE.cartSections = function () {
    const ids = [];
    if (qs('cart-drawer')) ids.push('cart-drawer');
    ids.push('cart-icon-bubble');
    return ids;
  };
  RIVIERE.addToCart = function (items, button) {
    if (button) { button.setAttribute('aria-busy', 'true'); button.classList.add('loading'); button.disabled = true; }
    const drawer = qs('cart-drawer');
    const onCartPage = !!document.getElementById('main-cart-items');
    const body = { items: items, sections: RIVIERE.cartSections(), sections_url: window.location.pathname };
    return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then((r) => r.json())
      .then((state) => {
        if (state.status && state.description) throw new Error(state.description);
        if (onCartPage) { window.location.reload(); return state; }
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.classList.remove('is-empty');
          drawer.renderContents(state);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'riviere', cartData: state });
        return state;
      })
      .catch((err) => {
        if (button) {
          const msg = button.closest('[data-riviere-add-scope]') && qs('[data-riviere-error]', button.closest('[data-riviere-add-scope]'));
          if (msg) { msg.textContent = err.message; msg.hidden = false; }
        }
        console.warn('[RIVIERE] add to cart', err);
      })
      .finally(() => {
        if (button) { button.removeAttribute('aria-busy'); button.classList.remove('loading'); button.disabled = false; }
      });
  };
  RIVIERE.changeLine = function (key, quantity) {
    const drawer = qs('cart-drawer');
    const onCartPage = !!document.getElementById('main-cart-items');
    return fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: RIVIERE.cartSections(), sections_url: window.location.pathname })
    })
      .then((r) => r.json())
      .then((state) => {
        if (onCartPage) { window.location.reload(); return; }
        if (drawer && drawer.renderContents) drawer.renderContents(state);
      });
  };

  /* quick add buttons: <button data-riviere-add="VARIANT_ID"> */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-riviere-add]');
    if (!btn) return;
    e.preventDefault();
    const id = parseInt(btn.dataset.riviereAdd, 10);
    if (!id) return;
    RIVIERE.addToCart([{ id: id, quantity: parseInt(btn.dataset.qty || '1', 10) }], btn);
  });

  /* ---------- <riviere-bundle> frequently bought together ---------- */
  class RiviereBundle extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.boxes = qsa('input[type="checkbox"][data-variant]', this);
      this.total = qs('[data-bundle-total]', this);
      this.compare = qs('[data-bundle-compare]', this);
      this.btn = qs('[data-bundle-add]', this);
      this.count = qs('[data-bundle-count]', this);
      this.boxes.forEach((b) => b.addEventListener('change', () => this.update()));
      qsa('select[data-bundle-variant]', this).forEach((sel) => sel.addEventListener('change', () => {
        const box = qs('input[data-variant][data-index="' + sel.dataset.index + '"]', this);
        const opt = sel.selectedOptions[0];
        box.dataset.variant = sel.value; box.dataset.price = opt.dataset.price; box.dataset.compare = opt.dataset.compare || opt.dataset.price;
        const priceEl = qs('[data-bundle-price="' + sel.dataset.index + '"]', this);
        if (priceEl) priceEl.textContent = RIVIERE.formatMoney(parseInt(opt.dataset.price, 10));
        this.update();
      }));
      if (this.btn) this.btn.addEventListener('click', () => {
        const items = this.boxes.filter((b) => b.checked).map((b) => ({ id: parseInt(b.dataset.variant, 10), quantity: 1 }));
        if (items.length) RIVIERE.addToCart(items, this.btn);
      });
      this.update();
    }
    update() {
      let sum = 0, cmp = 0, n = 0;
      this.boxes.forEach((b) => {
        const card = b.closest('[data-bundle-item]');
        if (card) card.classList.toggle('is-off', !b.checked);
        if (!b.checked) return;
        n++; sum += parseInt(b.dataset.price, 10) || 0; cmp += parseInt(b.dataset.compare || b.dataset.price, 10) || 0;
      });
      if (this.total) this.total.textContent = RIVIERE.formatMoney(sum);
      if (this.compare) { this.compare.textContent = cmp > sum ? RIVIERE.formatMoney(cmp) : ''; }
      if (this.count) this.count.textContent = n;
      if (this.btn) this.btn.disabled = n === 0;
    }
  }
  if (!customElements.get('riviere-bundle')) customElements.define('riviere-bundle', RiviereBundle);

  /* ---------- <riviere-cart-upsell> recommendations inside cart drawer / page ---------- */
  class RiviereCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      if (this.dataset.source !== 'recommendations' || !this.dataset.productId) { this.reveal(); return; }
      const inCart = (this.dataset.inCart || '').split(',').filter(Boolean);
      const limit = parseInt(this.dataset.limit || '6', 10);
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const url = root + 'recommendations/products.json?product_id=' + this.dataset.productId + '&limit=' + (limit + inCart.length) + '&intent=' + (this.dataset.intent || 'related');
      fetch(url)
        .then((r) => r.json())
        .then((data) => {
          const list = (data.products || []).filter((p) => inCart.indexOf(String(p.id)) === -1 && p.available).slice(0, limit);
          if (!list.length) { this.reveal(); return; }
          const track = qs('.riviere-rail__track', this) || qs('[data-upsell-list]', this);
          if (!track) return;
          track.innerHTML = list.map((p) => this.card(p)).join('');
          this.reveal(true);
        })
        .catch(() => this.reveal());
    }
    reveal(force) {
      const has = force || qsa('[data-upsell-item]', this).length > 0;
      this.hidden = !has;
      const rail = qs('riviere-rail', this);
      if (rail && rail.update) setTimeout(() => rail.update(), 50);
    }
    card(p) {
      const v = (p.variants || []).find((x) => x.available) || p.variants[0];
      const img = p.featured_image ? (p.featured_image.indexOf('?') > -1 ? p.featured_image + '&width=300' : p.featured_image + '?width=300') : '';
      const multi = p.variants.length > 1;
      const action = multi
        ? '<a class="riviere-upsell__btn" href="' + p.url + '">' + (this.dataset.chooseLabel || 'Choose') + '</a>'
        : '<button type="button" class="riviere-upsell__btn" data-riviere-add="' + v.id + '">' + (this.dataset.addLabel || 'Add') + '</button>';
      const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      return '<div class="riviere-upsell__item riviere-rail__item" data-upsell-item>' +
        '<a class="riviere-upsell__img" href="' + p.url + '">' + (img ? '<img src="' + img + '" alt="' + esc(p.title) + '" loading="lazy" width="300" height="375">' : '') + '</a>' +
        '<div class="riviere-upsell__info"><a class="riviere-upsell__title" href="' + p.url + '">' + esc(p.title) + '</a>' +
        '<span class="riviere-upsell__price">' + RIVIERE.formatMoney(v.price) + '</span>' + action + '</div></div>';
    }
  }
  if (!customElements.get('riviere-cart-upsell')) customElements.define('riviere-cart-upsell', RiviereCartUpsell);

  /* ---------- <riviere-giftwrap> toggle gift wrap product in cart ---------- */
  class RiviereGiftwrap extends HTMLElement {
    connectedCallback() {
      const box = qs('input[type="checkbox"]', this);
      if (!box || this._init) return;
      this._init = true;
      box.addEventListener('change', () => {
        box.disabled = true;
        if (box.checked) RIVIERE.addToCart([{ id: parseInt(this.dataset.variant, 10), quantity: 1 }]).finally(() => (box.disabled = false));
        else RIVIERE.changeLine(this.dataset.lineKey, 0).finally(() => (box.disabled = false));
      });
    }
  }
  if (!customElements.get('riviere-giftwrap')) customElements.define('riviere-giftwrap', RiviereGiftwrap);

  /* ---------- <riviere-pdp-addons> tick-box extras added with the product ---------- */
  class RivierePdpAddons extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        document.addEventListener('DOMContentLoaded', () => { this._init = false; this.connectedCallback(); }, { once: true });
        return;
      }
      this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
        if (!e || e.source !== 'product-form') return;
        const form = document.getElementById('product-form-' + this.dataset.section);
        const formVariant = form && form.querySelector('[name="id"]');
        if (formVariant && e.productVariantId && String(e.productVariantId) !== String(formVariant.value)) return;
        const boxes = qsa('input[data-addon-variant]:checked', this);
        if (!boxes.length) return;
        const items = boxes.map((b) => ({ id: parseInt(b.dataset.addonVariant, 10), quantity: 1 }));
        boxes.forEach((b) => { b.checked = false; });
        RIVIERE.addToCart(items);
      });
    }
    disconnectedCallback() { if (this.unsub) this.unsub(); }
  }
  if (!customElements.get('riviere-pdp-addons')) customElements.define('riviere-pdp-addons', RivierePdpAddons);

  /* ---------- Recently viewed ---------- */
  const RV_KEY = 'riviere_recent_v1';
  RIVIERE.recent = {
    get() { try { return JSON.parse(localStorage.getItem(RV_KEY) || '[]'); } catch (e) { return []; } },
    add(handle) {
      if (!handle) return;
      try {
        const list = RIVIERE.recent.get().filter((h) => h !== handle);
        list.unshift(handle);
        localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 16)));
      } catch (e) {}
    }
  };
  class RiviereRecent extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const current = this.dataset.current || '';
      const limit = parseInt(this.dataset.limit || '8', 10);
      const handles = RIVIERE.recent.get().filter((h) => h !== current).slice(0, limit);
      const section = this.closest('.shopify-section');
      if (!handles.length) { if (!isDesign() && section) section.hidden = true; return; }
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const track = qs('.riviere-rail__track', this);
      Promise.all(handles.map((h) => fetch(root + 'products/' + h + '?view=riviere-card').then((r) => (r.ok ? r.text() : '')).catch(() => '')))
        .then((cards) => {
          const html = cards.filter((c) => c && c.indexOf('riviere-rail__item') > -1).join('');
          if (!html) { if (!isDesign() && section) section.hidden = true; return; }
          track.innerHTML = html;
          this.hidden = false;
          const rail = qs('riviere-rail', this);
          if (rail && rail.update) setTimeout(() => rail.update(), 60);
        });
    }
  }
  if (!customElements.get('riviere-recent')) customElements.define('riviere-recent', RiviereRecent);

  /* ---------- Sticky add to cart ---------- */
  class RiviereStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const formId = this.dataset.form;
      this.mainBtn = () => document.querySelector('#' + formId + ' [name="add"], form#' + formId + ' button[type="submit"]') || qs('product-form .product-form__submit');
      this.target = () => qs('.product-form__buttons') || qs('.riviere-enquire') || qs('.product__info-container');
      const btn = qs('[data-sticky-add]', this);
      if (btn) btn.addEventListener('click', () => {
        const m = this.mainBtn();
        if (m && !m.disabled) m.click();
        else { const t = this.target(); if (t) t.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      });
      const scroller = qs('[data-sticky-scroll]', this);
      if (scroller) scroller.addEventListener('click', () => { const t = this.target(); if (t) t.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
      const t = this.target();
      if (!t || !('IntersectionObserver' in window)) return;
      const footer = qs('.footer');
      let pastTarget = false, footerVisible = false;
      const set = () => this.classList.toggle('is-visible', pastTarget && !footerVisible);
      new IntersectionObserver((en) => { const r = en[0]; pastTarget = !r.isIntersecting && r.boundingClientRect.top < 0; set(); }).observe(t);
      if (footer) new IntersectionObserver((en) => { footerVisible = en[0].isIntersecting; set(); }).observe(footer);
      // keep price / availability in sync with variant changes
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, (ev) => {
          const v = ev && ev.data && ev.data.variant;
          const price = qs('[data-sticky-price]', this);
          const label = qs('[data-sticky-variant]', this);
          if (v && price && typeof v.price === 'number') price.textContent = RIVIERE.formatMoney(v.price);
          if (v && label) label.textContent = v.title === 'Default Title' ? '' : v.title;
          if (btn) { btn.disabled = v ? !v.available : false; if (v && !v.available) btn.textContent = btn.dataset.soldout || btn.textContent; else if (btn.dataset.label) btn.textContent = btn.dataset.label; }
        });
      }
    }
  }
  if (!customElements.get('riviere-sticky-atc')) customElements.define('riviere-sticky-atc', RiviereStickyAtc);

  /* ---------- Delivery estimate ---------- */
  class RiviereDelivery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const min = parseInt(this.dataset.min || '2', 10), max = parseInt(this.dataset.max || '5', 10);
      const cutoff = parseInt(this.dataset.cutoff || '14', 10);
      const skip = (this.dataset.skip || '0').split(',').filter((x) => x !== '').map(Number);
      const tz = this.dataset.tz || undefined;
      const now = new Date();
      let hour = now.getHours();
      try { hour = parseInt(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: tz }).format(now), 10); } catch (e) {}
      const addBiz = (start, n) => {
        const d = new Date(start);
        let added = 0;
        while (added < n) { d.setDate(d.getDate() + 1); if (skip.indexOf(d.getDay()) === -1) added++; }
        return d;
      };
      const base = new Date(now);
      if (hour >= cutoff) base.setDate(base.getDate() + 1);
      const fmt = (d) => d.toLocaleDateString(document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      const a = addBiz(base, min), b = addBiz(base, max);
      const out = qs('[data-delivery-range]', this);
      if (out) out.textContent = min === max ? fmt(a) : fmt(a) + ' – ' + fmt(b);
      const cut = qs('[data-delivery-cutoff]', this);
      if (cut) {
        const left = cutoff - hour - (now.getMinutes() > 0 ? 1 : 0);
        if (hour < cutoff && left >= 0) {
          const mins = 60 - now.getMinutes();
          cut.textContent = (cut.dataset.template || 'Order within [time]').replace('[time]', (left > 0 ? left + 'h ' : '') + (mins === 60 ? 0 : mins) + 'm');
          cut.hidden = false;
        }
      }
    }
  }
  if (!customElements.get('riviere-delivery')) customElements.define('riviere-delivery', RiviereDelivery);

  /* ---------- <riviere-popup> newsletter / offer popup ---------- */
  class RivierePopup extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.key = 'riviere_popup_' + (this.dataset.id || 'x');
      this.dialog = qs('.riviere-popup__dialog', this);
      qsa('[data-popup-close]', this).forEach((b) => b.addEventListener('click', () => this.close(true)));
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(true); });
      if (this.dataset.success === 'true') { this.open(); return; }
      if (isDesign()) return;
      let last = 0;
      try { last = parseInt(localStorage.getItem(this.key) || '0', 10); } catch (e) {}
      const days = parseInt(this.dataset.days || '7', 10);
      if (Date.now() - last < days * 864e5) return;
      if (this.dataset.mobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;
      const delay = parseInt(this.dataset.delay || '8', 10) * 1000;
      this.t = setTimeout(() => this.open(), delay);
      if (this.dataset.exit === 'true') {
        document.addEventListener('mouseout', (e) => { if (!e.relatedTarget && e.clientY < 10) this.open(); }, { once: true });
      }
    }
    open() {
      if (this.isOpen || document.body.classList.contains('overflow-hidden')) return;
      this.isOpen = true;
      clearTimeout(this.t);
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      const f = qs('input[type="email"]', this);
      if (f && !isDesign()) setTimeout(() => f.focus({ preventScroll: true }), 400);
    }
    close(remember) {
      this.classList.remove('is-open');
      this.isOpen = false;
      setTimeout(() => (this.hidden = true), 500);
      if (remember) { try { localStorage.setItem(this.key, String(Date.now())); } catch (e) {} }
    }
  }
  if (!customElements.get('riviere-popup')) customElements.define('riviere-popup', RivierePopup);
  document.addEventListener('shopify:section:select', (e) => { const p = qs('#shopify-section-' + e.detail.sectionId + ' riviere-popup'); if (p) p.open(); });
  document.addEventListener('shopify:section:deselect', (e) => { const p = qs('#shopify-section-' + e.detail.sectionId + ' riviere-popup'); if (p) p.close(false); });

  /* ---------- Copy discount code ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-riviere-copy]');
    if (!b) return;
    const val = b.dataset.riviereCopy;
    const done = () => { const t = b.textContent; b.textContent = b.dataset.copied || 'Copied'; setTimeout(() => (b.textContent = t), 1600); };
    if (navigator.clipboard) navigator.clipboard.writeText(val).then(done).catch(() => {});
  });

  /* ---------- Wishlist (stored in the visitor's browser) ---------- */
  const WL_KEY = 'riviere_wishlist_v1';
  RIVIERE.wishlist = {
    get() { try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch (e) { return []; } },
    set(list) { try { localStorage.setItem(WL_KEY, JSON.stringify(list.slice(0, 60))); } catch (e) {} RIVIERE.wishlist.sync(); },
    has(h) { return RIVIERE.wishlist.get().indexOf(h) > -1; },
    toggle(h) {
      const list = RIVIERE.wishlist.get();
      const i = list.indexOf(h);
      if (i > -1) list.splice(i, 1); else list.unshift(h);
      RIVIERE.wishlist.set(list);
      return i === -1;
    },
    sync(root) {
      const list = RIVIERE.wishlist.get();
      qsa('[data-riviere-wish]', root || document).forEach((b) => {
        const on = list.indexOf(b.dataset.riviereWish) > -1;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on);
      });
      qsa('[data-wish-count]').forEach((c) => { c.textContent = list.length; c.hidden = list.length === 0; });
    }
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-riviere-wish]');
    if (!b) return;
    e.preventDefault(); e.stopPropagation();
    const added = RIVIERE.wishlist.toggle(b.dataset.riviereWish);
    b.classList.add('is-pop'); setTimeout(() => b.classList.remove('is-pop'), 400);
    const grid = b.closest('riviere-wishlist-grid');
    if (grid && !added) { const card = b.closest('li'); if (card) card.remove(); grid.empty(); }
  });
  new MutationObserver(raf(() => RIVIERE.wishlist.sync())).observe(document.documentElement, { childList: true, subtree: true });

  class RiviereWishlistGrid extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.grid = qs('[data-wish-grid]', this);
      const list = RIVIERE.wishlist.get();
      if (!list.length) { this.empty(); return; }
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      Promise.all(list.map((h) => fetch(root + 'products/' + h + '?view=riviere-card').then((r) => (r.ok ? r.text() : '')).catch(() => '')))
        .then((cards) => {
          this.grid.innerHTML = cards.filter((c) => c.indexOf('riviere-rail__item') > -1).join('').replace(/riviere-rail__item /g, '');
          RIVIERE.wishlist.sync(this);
          this.empty();
        });
    }
    empty() { const e = qs('[data-wish-empty]', this); if (e) e.hidden = !!qs('li', this.grid); }
  }
  if (!customElements.get('riviere-wishlist-grid')) customElements.define('riviere-wishlist-grid', RiviereWishlistGrid);

  /* ---------- Back-in-stock block follows the selected variant ---------- */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (ev) => {
      const v = ev && ev.data && ev.data.variant;
      qsa('[data-riviere-notify]').forEach((n) => {
        n.hidden = !!(v && v.available);
        const tags = qs('[data-riviere-notify-tags]', n);
        if (tags && v) tags.value = 'back-in-stock,notify-' + tags.dataset.handle + ',notify-variant-' + v.id;
      });
    });
  }

  /* ---------- Glide: rails move sideways while the page scrolls (mobile-first) ---------- */
  function initGlide() {
    if (reduceMotion.matches) return;
    const mq = window.matchMedia('(max-width: 989px)');
    let paused = new WeakMap();
    const rails = () => qsa('riviere-rail[data-riviere-glide]').filter((r) => {
      const m = r.dataset.riviereGlide;
      return m === 'all' || (m === 'mobile' && mq.matches);
    });
    const run = raf(() => {
      const vh = window.innerHeight;
      rails().forEach((r) => {
        const v = qs('.riviere-rail__viewport', r);
        if (!v) return;
        const max = v.scrollWidth - v.clientWidth;
        if (max <= 4) return;
        if (paused.get(v) && Date.now() - paused.get(v) < 2500) return;
        const rect = r.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        const p = clamp((vh - rect.top) / (vh + rect.height), 0, 1);
        const eased = clamp((p - 0.12) / 0.76, 0, 1);
        v.scrollLeft = eased * max;
      });
    });
    const pause = (e) => { const v = e.target.closest && e.target.closest('.riviere-rail__viewport'); if (v) paused.set(v, Date.now()); };
    document.addEventListener('touchstart', pause, { passive: true });
    document.addEventListener('pointerdown', pause, { passive: true });
    document.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) pause(e); }, { passive: true });
    window.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);
    run();
  }

  /* ---------- Pattern A helper: quick-view drawer ---------- */
  class RiviereQuickview extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.body = qs('[data-qv-body]', this);
      qsa('[data-qv-close]', this).forEach((b) => b.addEventListener('click', () => this.close()));
      document.addEventListener('keyup', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-riviere-qv]');
        if (!t) return;
        e.preventDefault();
        this.open(t.dataset.riviereQv, t);
      });
    }
    open(handle, trigger) {
      this.trigger = trigger;
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.body.classList.add('riviere-qv-lock');
      this.body.innerHTML = '<div class="riviere-qv__loading" aria-hidden="true"></div>';
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(root + 'products/' + encodeURIComponent(handle) + '?view=riviere-quick')
        .then((r) => r.text())
        .then((html) => { this.body.innerHTML = html; this.bind(); const f = qs('select, button', this.body); if (f) f.focus({ preventScroll: true }); })
        .catch(() => { this.body.innerHTML = '<p class="riviere-qv__error">Could not load this piece. <a href="' + root + 'products/' + handle + '">View it here</a>.</p>'; });
    }
    bind() {
      const json = qs('[data-qv-json]', this.body);
      const add = qs('[data-qv-add]', this.body);
      const price = qs('[data-qv-price]', this.body);
      if (!json || !add) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (e) {}
      const selects = qsa('[data-qv-option]', this.body);
      const update = () => {
        const vals = selects.map((s) => s.value);
        const v = variants.find((x) => x.options.every((o, i) => o === vals[i]));
        if (!v) { add.disabled = true; add.textContent = add.dataset.soldout; return; }
        add.dataset.riviereAdd = v.id;
        add.disabled = !v.available;
        add.textContent = v.available ? add.dataset.label : add.dataset.soldout;
        if (price) price.textContent = RIVIERE.formatMoney(v.price);
      };
      selects.forEach((s) => s.addEventListener('change', update));
      add.addEventListener('click', () => setTimeout(() => this.close(), 300));
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('riviere-qv-lock');
      setTimeout(() => { this.hidden = true; }, 450);
      if (this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  }
  if (!customElements.get('riviere-quickview')) customElements.define('riviere-quickview', RiviereQuickview);

  /* ---------- Pattern B: curtain mask reveal ---------- */
  class RiviereCurtain extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      if (reduceMotion.matches || isDesign() || !('IntersectionObserver' in window)) { this.classList.add('is-open'); return; }
      const io = new IntersectionObserver((en) => {
        en.forEach((e) => { if (e.isIntersecting) { this.classList.add('is-open'); io.disconnect(); } });
      }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });
      io.observe(this.parentElement || this);
    }
  }
  if (!customElements.get('riviere-curtain')) customElements.define('riviere-curtain', RiviereCurtain);

  /* ---------- Pattern C: split-screen sticky story chapters ---------- */
  class RiviereStickyStory extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const frames = qsa('[data-ss-step]', this);
      const chapters = qsa('[data-ss-chapter]', this);
      if (!frames.length || !chapters.length || !('IntersectionObserver' in window)) return;
      const set = (i) => chapters.forEach((c) => c.classList.toggle('is-active', c.dataset.ssChapter === String(i)));
      const io = new IntersectionObserver((en) => {
        en.forEach((e) => { if (e.isIntersecting) set(e.target.dataset.ssStep); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      frames.forEach((f) => io.observe(f));
      chapters.forEach((c) => c.addEventListener('click', () => {
        const f = frames.find((x) => x.dataset.ssStep === c.dataset.ssChapter);
        if (f) f.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      }));
    }
  }
  if (!customElements.get('riviere-sticky-story')) customElements.define('riviere-sticky-story', RiviereStickyStory);

  /* ---------- Pattern D: scroll-scrubbed 360° spin (canvas frames or video) ---------- */
  class RiviereSpin extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.mode = this.dataset.mode;
      this.turns = parseInt(this.dataset.turns || '1', 10);
      this.mq = window.matchMedia('(max-width: 749px)');
      this.auto = this.dataset.mobile === 'auto' && this.mq.matches;
      this.bar = qs('.riviere-spin__progress', this);
      this.caps = qsa('.riviere-spin__caption', this);
      if (this.auto || reduceMotion.matches) this.classList.add('riviere-spin--static');
      if (this.mode === 'frames') this.initFrames(); else this.initVideo();
      this.onScroll = raf(() => this.render());
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.render();
    }
    progress() {
      if (this.classList.contains('riviere-spin--static')) return null;
      const r = this.getBoundingClientRect();
      const total = this.offsetHeight - window.innerHeight;
      return total > 0 ? clamp(-r.top / total, 0, 1) : 0;
    }
    initFrames() {
      this.canvas = qs('canvas', this);
      if (!this.canvas) return;
      this.ctx = this.canvas.getContext('2d');
      const count = parseInt(this.dataset.count || '0', 10), start = parseInt(this.dataset.start || '1', 10), pad = parseInt(this.dataset.pad || '2', 10);
      const pattern = this.dataset.pattern || '';
      this.frames = [];
      let loaded = 0;
      const loader = qs('.riviere-spin__loader span', this);
      const urls = Array.from({ length: count }, (_, i) => pattern.replace('[n]', String(i + start).padStart(pad, '0')));
      const load = (i) => new Promise((res) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = img.onerror = () => { loaded++; if (loader) loader.style.transform = 'scaleX(' + loaded / count + ')'; res(); };
        img.src = urls[i];
        this.frames[i] = img;
      });
      // first frame now, the rest when the section approaches the viewport
      load(0).then(() => { this.draw(0); this.classList.add('is-ready'); });
      const io = new IntersectionObserver((en) => {
        if (!en[0].isIntersecting) return;
        io.disconnect();
        const chain = [];
        for (let i = 1; i < count; i++) chain.push(load(i));
        Promise.all(chain).then(() => { this.classList.add('is-loaded'); this.render(); });
      }, { rootMargin: '600px 0px' });
      io.observe(this);
      if (this.classList.contains('riviere-spin--static')) {
        let i = 0;
        const tick = () => { if (this.isConnected) { this.draw(i = (i + 1) % count); setTimeout(() => requestAnimationFrame(tick), 70); } };
        setTimeout(tick, 1200);
      }
    }
    draw(i) {
      const img = this.frames && this.frames[i];
      if (!img || !img.complete || !img.naturalWidth) return;
      const c = this.canvas, ctx = this.ctx;
      if (c.width !== img.naturalWidth) { c.width = img.naturalWidth; c.height = img.naturalHeight; }
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0);
      this.current = i;
    }
    initVideo() {
      this.video = qs('video', this);
      if (!this.video) return;
      this.video.muted = true;
      this.video.pause();
      if (this.classList.contains('riviere-spin--static')) { this.video.loop = true; this.video.play().catch(() => {}); }
      this.video.addEventListener('loadedmetadata', () => { this.classList.add('is-ready'); this.render(); });
    }
    render() {
      const p = this.progress();
      if (p === null) return;
      if (this.bar) this.bar.style.setProperty('--riviere-p', p.toFixed(4));
      const pct = p * 100;
      this.caps.forEach((c) => c.classList.toggle('is-on', pct >= +c.dataset.from && pct < (+c.dataset.to || 100.01)));
      const spin = (p * this.turns) % 1;
      if (this.mode === 'frames' && this.frames && this.frames.length) {
        const i = Math.min(this.frames.length - 1, Math.round(spin * (this.frames.length - 1)));
        if (i !== this.current) this.draw(i);
      } else if (this.video && this.video.duration) {
        const t = spin * (this.video.duration - 0.05);
        if (Math.abs(this.video.currentTime - t) > 0.02) this.video.currentTime = t;
      }
    }
  }
  if (!customElements.get('riviere-spin')) customElements.define('riviere-spin', RiviereSpin);

  /* ---------- Contact form: prefill inquiry from ?product= ---------- */
  function prefillEnquiry() {
    const params = new URLSearchParams(window.location.search);
    const product = params.get('product');
    if (!product) return;
    const form = qs('#ContactForm') || qs('form[action*="contact"]');
    if (!form) return;
    const body = qs('[name="contact[body]"]', form);
    if (body && !body.value) body.value = 'I would like to know more about: ' + product + '\n\n';
    if (!qs('[name="contact[Product]"]', form)) {
      const h = document.createElement('input');
      h.type = 'hidden'; h.name = 'contact[Product]'; h.value = product;
      form.appendChild(h);
    }
  }

  /* ---------- Boot ---------- */
  function boot() {
    prefillEnquiry();
    RIVIERE.wishlist.sync();
    initGlide();
    initHeader();
    RIVIERE.reveal();
    initParallax();
    const rv = qs('[data-riviere-track-product]');
    if (rv) RIVIERE.recent.add(rv.dataset.riviereTrackProduct);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    RIVIERE.reveal(e.target);
    if (RIVIERE.parallax) RIVIERE.parallax();
    if (e.target.querySelector('.section-header, .riviere-header') || e.target.classList.contains('section-header')) initHeader();
  });
})();
